import React, { useContext } from "react";
import PropTypes from "prop-types";
import { PromoContext } from "@context/PromoContext";
import { SessionContext } from "@context/SessionContext";
import { findTicketPriceRule } from "@utils/promoUtils";
import { getVoucherUpgradeDetails } from "@utils/ticketUtils";
import "./style.scss";

const calcTotalPrice = (carry, item) => {
    return item.promo
        ? carry
        : carry + (item.price_cents * item.quantity);
};
const calcSelectedPromoPrice = (carry, item, promoPrices, session) => {
    const itemName = item.ticket_type_id && Number(item.ticket_type_id) === 5 ? "child" : "adult";
    const matchedRule = findTicketPriceRule(promoPrices, item.ticket_class_id, session);
    const promoPrice = matchedRule?.[`${itemName}_price_cents`];
    const price = promoPrice > 0 ? promoPrice : item.price_cents;

    return item.promo
        ? carry
        : carry + (price * item.quantity);
};
const calcPrePromoSavings = (carry, item, promoPrices, appliedPromo, session) => {
    const itemName = item.ticket_type_id && Number(item.ticket_type_id) === 5 ? "child" : "adult";
    const matchedRule = findTicketPriceRule(promoPrices, item.ticket_class_id, session);
    const promoPrice = matchedRule?.[`${itemName}_price_cents`];
    const price = promoPrice > 0 ? promoPrice : item.price_cents;
    const rrp = Number.isFinite(item.rrp_pricing_cents) ? item.rrp_pricing_cents : null;
    const savingPerItem = appliedPromo || rrp === null ? item.saving : rrp - price;

    return item.promo ? carry : carry + (savingPerItem * item.quantity);
};

const calcTotalQuantity = (carry, item) => {
    return carry + item.quantity;
};

const calcTicketQuantity = (carry, item) => {
    return item.type === "ticket" ? carry + item.quantity : carry;
};

const BookingTotal = ({ items }) => {
    const { appliedPromo, selectedPromo } = useContext(PromoContext);
    const { session } = useContext(SessionContext);
    const promoPrices = selectedPromo?.promo_code_ticket_price || [];

    // Pre promo amounts
    let subTotal = Math.max(items.reduce(calcTotalPrice, 0), 0);
    const selectedPromoTotal = selectedPromo ? Math.max(items.reduce((acc, item) => calcSelectedPromoPrice(acc, item, promoPrices, session), 0), 0) : 0;
    subTotal = !appliedPromo && selectedPromoTotal > 0 ? selectedPromoTotal : subTotal;
    const prePromoSavings = items.reduce((acc, item) => calcPrePromoSavings(acc, item, promoPrices, Boolean(appliedPromo), session), 0);

    // Promo amounts
    const discount =  appliedPromo?.applied ? appliedPromo?.discount : 0;

    // Net totals
    const totalQuantity = items.reduce(calcTotalQuantity, 0);
    const ticketQuantity = items.reduce(calcTicketQuantity, 0);

    const upgradeDetails = session ? getVoucherUpgradeDetails(session) : null;
    const upgradeFeeCents = upgradeDetails ? upgradeDetails.fee * 100 * ticketQuantity : 0;

    const totalSavings = prePromoSavings + discount - upgradeFeeCents;
    const finalTotal = Math.max(subTotal - discount, 0);

    return (
        <>
            {totalQuantity > 0 && totalSavings > 0 && (
                <div className="form__group-wrapper">
                    <div className="booking__label booking__label--bold">
                        Savings:
                        {upgradeDetails?.fee > 0 && (
                            <span className="booking__label-note">
                                {" "}(after ${upgradeDetails.fee.toFixed(2)} upgrade fee)
                            </span>
                        )}
                    </div>
                    <div className={`booking__price booking__price--bold ${totalQuantity === 0 ? "booking__quantity-none" : ""}`}>
                        ${(totalSavings / 100).toFixed(2)}
                    </div>
                </div>
            )}
            <div className="form__group-wrapper">
                <div className={`booking__label ${discount > 0 ? "booking__label--bold" : ""}`}>Total</div>
                <div className={`booking__price ${discount > 0 ? "booking__price--bold" : ""} ${totalQuantity === 0 ? "booking__quantity-none" : ""}`}>
                    ${(finalTotal / 100).toFixed(2)}
                </div>
            </div>
        </>

    );
};

BookingTotal.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            promo: PropTypes.bool.isRequired,
            price_cents: PropTypes.number.isRequired,
            quantity: PropTypes.number.isRequired
        })
    ).isRequired,
};

export default BookingTotal;
